import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import { ADD_UPDATE_COMPANY_DETAILS } from "../../services/userApi";
import "../../Home/global.css";

const CompanyCard = ({
  companyName,
  totalLeads,
  headquarters,
  companyDetails,
  roleId,
  onViewDetails,
  onSaveComplete,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [orderValue, setOrderValue] = useState("");
  const [closureDate, setClosureDate] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const [addUpdateCompanyDetails, { loading: saving }] = useMutation(
    ADD_UPDATE_COMPANY_DETAILS,
    {
      onCompleted: () => {
        setModalOpen(false);
        setDocumentFile(null);
        setFileError("");
        if (onSaveComplete) onSaveComplete();
      },
      onError: (err) => {
        console.error("Error saving company details:", err);
      },
    }
  );

  const hasDetails =
    companyDetails &&
    (companyDetails.orderValue != null ||
      companyDetails.closureDate ||
      companyDetails.companyContract);

  const formatOrderDisplay = (raw) => {
    if (raw === "" || raw == null) return "";
    const digits = String(raw).replace(/,/g, "");
    if (!digits) return "";
    const num = parseInt(digits, 10);
    return isNaN(num) ? "" : num.toLocaleString("en-IN");
  };

  const handleOrderValueChange = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    if (digits === "") { setOrderValue(""); return; }
    const num = parseInt(digits, 10);
    setOrderValue(isNaN(num) ? "" : num.toLocaleString("en-IN"));
  };

  const handleOpenModal = () => {
    setOrderValue(formatOrderDisplay(companyDetails?.orderValue));
    setClosureDate(companyDetails?.closureDate ?? "");
    setDocumentFile(null);
    setFileError("");
    setModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        setFileError("Only PDF, DOC, DOCX, JPG, PNG files are allowed.");
        setDocumentFile(null);
        return;
      }
      setFileError("");
      setDocumentFile(file);
    }
  };

  const handleSave = () => {
    const resolvedRoleId =
      parseInt(companyDetails?.roleId, 10) ||
      parseInt(roleId, 10) ||
      parseInt(sessionStorage.getItem("dashboardRoleId"), 10);

    const variables = {
      companyName,
      roleId: resolvedRoleId,
    };
    if (companyDetails?.id) {
      variables.companyId = parseInt(companyDetails.id, 10);
    }
    const rawOrderValue = String(orderValue).replace(/,/g, "");
    if (rawOrderValue !== "") {
      variables.orderValue = parseInt(rawOrderValue, 10);
    }
    if (closureDate) {
      variables.closureDate = closureDate;
    }
    if (documentFile) {
      variables.document = documentFile;
    }
    addUpdateCompanyDetails({ variables });
  };

  const formatCompanyName = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const contractUrl = companyDetails?.documentUrl;

  return (
    <>
      {/* h-full ensures all cards in the grid row stretch to the same height */}
      <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-200 global flex flex-col h-full">

        {/* ── TOP: Company Name ── */}
        <h3 className="text-lg font-semibold text-gray-900 truncate mb-3">
          {formatCompanyName(companyName)}
        </h3>

        {/* ── MIDDLE: grows to fill space ── */}
        <div className="flex flex-col gap-2 flex-1">
          {/* Total Leads + View Leads */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>
                Total Leads:{" "}
                <span className="font-semibold text-gray-900">{totalLeads.toLocaleString()}</span>
              </span>
            </div>
            <button
              onClick={onViewDetails}
              className="px-4 py-2 border border-red-500 text-red-500 rounded-md hover:bg-red-50 transition-colors duration-200 text-sm font-medium whitespace-nowrap"
            >
              View Leads
            </button>
          </div>

          {/* Headquarters — always reserves one line of space */}
          <div className="flex items-center gap-2 text-sm text-gray-600 min-h-[22px]">
            {headquarters ? (
              <>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="truncate">
                  HQ: <span className="font-medium">{headquarters}</span>
                </span>
              </>
            ) : null}
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div className="border-t border-gray-100 my-3" />

        {/* ── BOTTOM: Contract Details — fixed 3-row layout ── */}
        <div className="flex flex-col gap-2">
          {/* Row 1: Order Value */}
          <div className="flex items-center gap-2 text-sm text-gray-600 min-h-[20px]">
            <svg className="w-4 h-4 flex-shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {companyDetails?.orderValue != null ? (
              <span>
                Order Value:{" "}
                <span className="font-semibold text-gray-900">
                  ₹{Number(companyDetails.orderValue).toLocaleString("en-IN")}
                </span>
              </span>
            ) : (
              <span className="text-gray-400 italic">Order value not set</span>
            )}
          </div>

          {/* Row 2: Closure Date */}
          <div className="flex items-center gap-2 text-sm text-gray-600 min-h-[20px]">
            <svg className="w-4 h-4 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {companyDetails?.closureDate ? (
              <span>
                Closure:{" "}
                <span className="font-semibold text-gray-900">{formatDate(companyDetails.closureDate)}</span>
              </span>
            ) : (
              <span className="text-gray-400 italic">Closure date not set</span>
            )}
          </div>

          {/* Row 3: Contract */}
          <div className="flex items-center gap-2 text-sm text-gray-600 min-h-[20px]">
            <svg className="w-4 h-4 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {contractUrl ? (
              <a href={contractUrl} download rel="noopener noreferrer"
                className="text-red-500 hover:underline font-medium truncate">
                Download Contract
              </a>
            ) : (
              <span className="text-gray-400 italic">No contract uploaded</span>
            )}
          </div>

          {/* Action Button */}
          <button
            onClick={handleOpenModal}
            className={`mt-1 w-full px-3 py-1.5 rounded-md text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
              hasDetails
                ? "border border-gray-300 text-gray-600 hover:border-red-400 hover:text-red-500"
                : "border border-dashed border-red-400 text-red-500 hover:bg-red-50"
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {hasDetails ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              )}
            </svg>
            {hasDetails ? "Edit Contract Details" : "Add Contract Details"}
          </button>
        </div>
      </div>

      {/* ── MODAL ── */}
      <Dialog
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.05rem", borderBottom: "1px solid #f0f0f0", pb: 1.5 }}>
          {hasDetails ? "Edit" : "Add"} Contract Details —{" "}
          <span style={{ color: "#dc3545" }}>{formatCompanyName(companyName)}</span>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: "24px !important", pb: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Order Value */}
            <TextField
              label="Order Value (₹)"
              type="text"
              value={orderValue}
              onChange={handleOrderValueChange}
              fullWidth
              size="small"
              placeholder="e.g. 10,00,000"
              inputProps={{ inputMode: "numeric" }}
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#dc3545" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#dc3545" },
              }}
            />

            {/* Closure Date */}
            <TextField
              label="Closure Date"
              type="date"
              value={closureDate}
              onChange={(e) => setClosureDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#dc3545" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#dc3545" },
              }}
            />

            {/* Document Upload */}
            <div>
              <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: 6, fontWeight: 500 }}>
                Upload Contract{" "}
                {contractUrl && (
                  <a href={contractUrl} download rel="noopener noreferrer"
                    style={{ color: "#dc3545", textDecoration: "none", fontWeight: 400 }}>
                    (download existing)
                  </a>
                )}
              </p>
              <label
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  width: "100%", border: "2px dashed #d1d5db", borderRadius: 8, padding: "16px 12px",
                  cursor: "pointer", transition: "border-color 0.2s, background 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#dc3545"; e.currentTarget.style.background = "#fff5f5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.background = "transparent"; }}
              >
                <svg className="w-6 h-6 mb-1" style={{ color: "#9ca3af" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span style={{ fontSize: "0.82rem", color: documentFile ? "#1f2937" : "#6b7280" }}>
                  {documentFile ? documentFile.name : "Click to upload  PDF, DOC, DOCX, JPG, PNG"}
                </span>
                <input type="file" style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange} />
              </label>
              {fileError && (
                <p style={{ fontSize: "0.75rem", color: "#dc3545", marginTop: 4 }}>{fileError}</p>
              )}
            </div>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button onClick={() => setModalOpen(false)} disabled={saving} variant="outlined"
            sx={{ borderColor: "#888", color: "#555", "&:hover": { borderColor: "#dc3545", color: "#dc3545" }, textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !!fileError} variant="contained"
            sx={{ bgcolor: "#dc3545", "&:hover": { bgcolor: "#b02a37" }, textTransform: "none", minWidth: 90 }}>
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyCard;
